import React, { useEffect, useState, Children, useRef } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const AdvancedFilter = ({
  fields,
  onApply,
  onClear,
  filters: externalFilters = [],
  children,
  triggerLabel = 'Advanced Filters',
  applyLabel = 'Apply Filters',
  onValidate = null,
  hasFilterValues = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState(externalFilters.length > 0 ? externalFilters : [
    { field: '', operator: 'contains', value: '' }
  ]);
  const [validationError, setValidationError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const dateInputRefs = useRef({});

  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    setFilters(externalFilters.length > 0 ? externalFilters : [{ field: '', operator: 'contains', value: '' }]);
  }, [externalFilters, isOpen]);

  // Define operators based on field type
  const getOperatorsForField = (fieldValue) => {
    const field = fields.find(f => f.value === fieldValue);
    const isDateField = field?.type === 'date' || fieldValue === 'DOB' || fieldValue === 'DateOfBirth';
    
    if (isDateField) {
      return [
        { value: 'greaterThan', label: 'Greater Than' },
        { value: 'lessThan', label: 'Less Than' },
        { value: 'between', label: 'Between' },
        { value: 'equals', label: 'Equals' },
        { value: 'notEquals', label: 'Not Equals' }
      ];
    }
    
    return [
      { value: 'contains', label: 'Contains' },
      { value: 'notContains', label: 'Not Contains' },
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'startsWith', label: 'Starts With' },
      { value: 'endsWith', label: 'Ends With' },
      { value: 'greaterThan', label: 'Greater Than' },
      { value: 'lessThan', label: 'Less Than' },
      { value: 'between', label: 'Between' }
    ];
  };

  // Validate date format (YYYY-MM-DD)
  const validateDateFormat = (value) => {
    if (!value) return true;
    const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
    return dateRegex.test(value);
  };

  // Validate date value is valid
  const isValidDate = (value) => {
    if (!value) return true;
    const parts = value.split('-');
    if (parts.length !== 3) return false;
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const day = parseInt(parts[2]);
    const date = new Date(year, month, day);
    return date.getFullYear() === year && 
           date.getMonth() === month && 
           date.getDate() === day;
  };

  // Format date with auto-hyphens (YYYY-MM-DD)
  const formatDateWithHyphens = (value) => {
    if (!value) return '';
    // Remove any non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length === 0) return '';
    
    // Build the formatted string
    let formatted = '';
    for (let i = 0; i < cleaned.length && i < 8; i++) {
      if (i === 4 || i === 6) {
        formatted += '-';
      }
      formatted += cleaned[i];
    }
    
    // If we have all 8 digits, add the final dash if needed
    if (cleaned.length > 8) {
      // Limit to 8 digits (YYYYMMDD)
      return formatted.slice(0, 10);
    }
    
    return formatted;
  };

  const handleDateInput = (index, value, isStart = true) => {
    const filter = filters[index];
    const isDateField = filter.field === 'DOB' || filter.field === 'DateOfBirth';
    
    if (!isDateField) {
      updateFilter(index, 'value', value);
      return;
    }

    // Auto-format with hyphens
    const formatted = formatDateWithHyphens(value);
    
    if (filter.operator === 'between') {
      const currentValue = Array.isArray(filter.value) ? filter.value : ['', ''];
      const newValue = [...currentValue];
      newValue[isStart ? 0 : 1] = formatted;
      updateFilter(index, 'value', newValue);
      
      // Clear error on change
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${index}-${isStart ? 'start' : 'end'}`];
        return newErrors;
      });
    } else {
      updateFilter(index, 'value', formatted);
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const openDatePicker = (index, isStart = true) => {
    const key = `${index}-${isStart ? 'start' : 'end'}`;
    const input = dateInputRefs.current[key];
    if (input) {
      input.showPicker ? input.showPicker() : input.focus();
    }
  };

  const addFilter = () => {
    setFilters([...filters, { field: '', operator: 'contains', value: '' }]);
    setValidationError('');
    setFieldErrors({});
  };

  const removeFilter = (index) => {
    if (filters.length === 1) return;
    setFilters(filters.filter((_, i) => i !== index));
    setValidationError('');
    setFieldErrors({});
  };

  const updateFilter = (index, key, value) => {
    const updated = [...filters];
    updated[index] = { ...updated[index], [key]: value };
    
    if (key === 'field') {
      const isDateField = value === 'DOB' || value === 'DateOfBirth';
      updated[index].operator = isDateField ? 'greaterThan' : 'contains';
      updated[index].value = '';
    }
    
    setFilters(updated);
    setValidationError('');
    
    if (key === 'field') {
      setFieldErrors({});
    }
  };

  const validateFilterValue = (filter, index) => {
    const isDateField = filter.field === 'DOB' || filter.field === 'DateOfBirth';
    
    if (!isDateField) return true;
    
    if (filter.operator === 'between') {
      if (Array.isArray(filter.value)) {
        const [start, end] = filter.value;
        if (start && !validateDateFormat(start)) {
          setFieldErrors(prev => ({
            ...prev,
            [`${index}-start`]: 'Use format: YYYY-MM-DD'
          }));
          return false;
        }
        if (start && !isValidDate(start)) {
          setFieldErrors(prev => ({
            ...prev,
            [`${index}-start`]: 'Invalid date'
          }));
          return false;
        }
        if (end && !validateDateFormat(end)) {
          setFieldErrors(prev => ({
            ...prev,
            [`${index}-end`]: 'Use format: YYYY-MM-DD'
          }));
          return false;
        }
        if (end && !isValidDate(end)) {
          setFieldErrors(prev => ({
            ...prev,
            [`${index}-end`]: 'Invalid date'
          }));
          return false;
        }
        if (start && end && isValidDate(start) && isValidDate(end)) {
          const startDate = new Date(start.split('-')[0], parseInt(start.split('-')[1]) - 1, parseInt(start.split('-')[2]));
          const endDate = new Date(end.split('-')[0], parseInt(end.split('-')[1]) - 1, parseInt(end.split('-')[2]));
          if (startDate > endDate) {
            setValidationError('Start date must be before end date');
            return false;
          }
        }
      }
    } else {
      if (filter.value && !validateDateFormat(filter.value)) {
        setFieldErrors(prev => ({
          ...prev,
          [index]: 'Use format: YYYY-MM-DD'
        }));
        return false;
      }
      if (filter.value && !isValidDate(filter.value)) {
        setFieldErrors(prev => ({
          ...prev,
          [index]: 'Invalid date'
        }));
        return false;
      }
    }
    
    return true;
  };

  const handleApply = () => {
    if (onValidate) {
      const externalError = onValidate();
      if (externalError) {
        setValidationError(externalError);
        return;
      }
    }

    const hasChildFilters = hasFilterValues;
    const filtersWithField = filters.filter(f => f.field);
    
    let hasDateError = false;
    for (let i = 0; i < filtersWithField.length; i++) {
      const filter = filtersWithField[i];
      const isDateField = filter.field === 'DOB' || filter.field === 'DateOfBirth';
      
      if (isDateField && filter.value) {
        if (!validateFilterValue(filter, i)) {
          hasDateError = true;
        }
      }
    }
    
    if (hasDateError) {
      return;
    }
    
    const validFilters = filtersWithField.filter(f => {
      if (f.operator === 'between') {
        return Array.isArray(f.value) && f.value.some(v => v);
      }
      return f.value;
    });
    
    const invalidFilters = filtersWithField.filter(f => {
      if (f.operator === 'between') {
        return !(Array.isArray(f.value) && f.value.some(v => v));
      }
      return !f.value;
    });
    
    if (invalidFilters.length > 0) {
      setValidationError('Please enter a value for all selected filter fields');
      return;
    }
    
    if (!hasChildFilters && validFilters.length === 0) {
      setValidationError('Please select at least one filter option or add a filter rule');
      return;
    }
    
    setValidationError('');
    setFieldErrors({});
    onApply(validFilters);
    setIsOpen(false);
  };

  const handleClearAll = () => {
    setFilters([{ field: '', operator: 'contains', value: '' }]);
    setValidationError('');
    setFieldErrors({});
    onClear();
  };

  const handleClose = () => {
    setValidationError('');
    setFieldErrors({});
    setIsOpen(false);
  };

  const getAvailableFields = (currentIndex) => {
    const selectedFields = filters
      .map((f, i) => i !== currentIndex && f.field ? f.field : null)
      .filter(Boolean);
    return fields.filter(f => !selectedFields.includes(f.value));
  };

  const activeFilterCount = filters.filter(f => f.field && f.value).length;
  const childrenArray = Children.toArray(children);

  const renderFilterChildren = () => {
    if (!childrenArray.length) return null;

    const firstRowItems = childrenArray.slice(0, 2);
    const secondRowItems = childrenArray.slice(2, 6);

    return (
      <>
        <div className="row g-3 mb-3">
          {firstRowItems.map((child, index) => (
            <div className="col-md-6" key={index}>
              {child}
            </div>
          ))}
          {firstRowItems.length < 2 && (
            <>
              {[...Array(2 - firstRowItems.length)].map((_, index) => (
                <div className="col-md-6" key={`empty-${index}`}></div>
              ))}
            </>
          )}
        </div>

        {secondRowItems.length > 0 && (
          <div className="row g-3">
            {secondRowItems.map((child, index) => (
              <div className="col-md-3" key={`second-${index}`}>
                {child}
              </div>
            ))}
            {secondRowItems.length < 4 && (
              <>
                {[...Array(4 - secondRowItems.length)].map((_, index) => (
                  <div className="col-md-3" key={`empty-second-${index}`}></div>
                ))}
              </>
            )}
          </div>
        )}
      </>
    );
  };

  const renderFilterInput = (filter, index) => {
    const isDateField = filter.field === 'DOB' || filter.field === 'DateOfBirth';
    const error = fieldErrors[index] || fieldErrors[`${index}-start`] || fieldErrors[`${index}-end`];

    if (filter.operator === 'between') {
      const startKey = `${index}-start`;
      const endKey = `${index}-end`;
      
      return (
        <div className="d-flex gap-1">
          <div style={{ flex: 1 }} className="date-input-wrapper">
            <input
              ref={el => dateInputRefs.current[startKey] = el}
              type={isDateField ? "date" : "text"}
              className={`form-control form-control-sm radius-8 border-1 ${fieldErrors[startKey] ? 'is-invalid' : ''}`}
              placeholder={isDateField ? "YYYY-MM-DD" : "Start"}
              value={Array.isArray(filter.value) ? filter.value[0] || '' : ''}
              onChange={(e) => {
                if (isDateField) {
                  const current = Array.isArray(filter.value) ? filter.value : ['', ''];
                  const newValue = [e.target.value, current[1] || ''];
                  updateFilter(index, 'value', newValue);
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[startKey];
                    return newErrors;
                  });
                } else {
                  handleDateInput(index, e.target.value, true);
                }
              }}
            />
            {isDateField && (
              <button 
                type="button"
                className="date-picker-icon"
                onClick={() => openDatePicker(index, true)}
              >
              </button>
            )}
            {fieldErrors[startKey] && (
              <div className="invalid-feedback d-block" style={{ fontSize: '11px' }}>
                {fieldErrors[startKey]}
              </div>
            )}
          </div>
          <div style={{ flex: 1 }} className="date-input-wrapper">
            <input
              ref={el => dateInputRefs.current[endKey] = el}
              type={isDateField ? "date" : "text"}
              className={`form-control form-control-sm radius-8 border-1 ${fieldErrors[endKey] ? 'is-invalid' : ''}`}
              placeholder={isDateField ? "YYYY-MM-DD" : "End"}
              value={Array.isArray(filter.value) ? filter.value[1] || '' : ''}
              onChange={(e) => {
                if (isDateField) {
                  const current = Array.isArray(filter.value) ? filter.value : ['', ''];
                  const newValue = [current[0] || '', e.target.value];
                  updateFilter(index, 'value', newValue);
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[endKey];
                    return newErrors;
                  });
                } else {
                  handleDateInput(index, e.target.value, false);
                }
              }}
            />
            {isDateField && (
              <button 
                type="button"
                className="date-picker-icon"
                onClick={() => openDatePicker(index, false)}
              >
              </button>
            )}
            {fieldErrors[endKey] && (
              <div className="invalid-feedback d-block" style={{ fontSize: '11px' }}>
                {fieldErrors[endKey]}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="date-input-wrapper">
        <input
          ref={el => dateInputRefs.current[index] = el}
          type={isDateField ? "date" : "text"}
          className={`form-control form-control-sm radius-8 border-1 ${error ? 'is-invalid' : ''}`}
          placeholder={isDateField ? "YYYY-MM-DD" : "Enter value..."}
          value={filter.value}
          onChange={(e) => {
            if (isDateField) {
              updateFilter(index, 'value', e.target.value);
              setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[index];
                return newErrors;
              });
            } else {
              handleDateInput(index, e.target.value, true);
            }
          }}
          style={{ backgroundColor: '#fff' }}
        />
        {isDateField && (
          <button 
            type="button"
            className="date-picker-icon"
            onClick={() => openDatePicker(index, true)}
          >
          </button>
        )}
        {error && (
          <div className="invalid-feedback d-block" style={{ fontSize: '11px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        className="btn btn-outline-primary btn-sm h-40-px d-inline-flex align-items-center gap-2 position-relative"
        onClick={() => setIsOpen(true)}
      >
        <Icon icon="ion:funnel-outline" className="icon" />
        {triggerLabel}
        {(activeFilterCount > 0 || hasFilterValues) && (
          <span className="badge bg-primary text-white rounded-pill ms-1">
            {activeFilterCount + (hasFilterValues ? 1 : 0)}
          </span>
        )}
      </button>

      <div 
        className={`modal fade ${isOpen ? 'show d-block' : ''}`} 
        style={{ 
          display: isOpen ? 'block' : 'none',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1050,
          overflow: 'hidden'
        }}
      >
        <div 
          className="modal-dialog modal-xl modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '1000px' }}
        >
          <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', maxHeight: '80vh' }}>
            <div className="modal-header py-16 px-20 border-bottom" style={{ backgroundColor: '#f8fafc' }}>
              <h4 className="modal-title text-lg fw-semibold text-primary-light d-flex align-items-center gap-2">
                <Icon icon="ion:funnel-outline" className="icon text-xl" />
                Advanced Filters
              </h4>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
              />
            </div>

            <div className="modal-body p-20" style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 130px)' }}>
              {validationError && (
                <div className="alert alert-danger alert-dismissible fade show mb-3 d-flex align-items-center" role="alert">
                  <Icon icon="ion:alert-circle-outline" className="me-2" />
                  {validationError}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setValidationError('')}
                    aria-label="Close"
                  />
                </div>
              )}

              {childrenArray.length > 0 && (
                <div className="mb-20">
                  <div className="d-flex align-items-center gap-2 mb-12">
                    <div className="flex-grow-1 border-bottom"></div>
                    <span className="text-secondary-light text-sm fw-medium px-2">Filter Options</span>
                    <div className="flex-grow-1 border-bottom"></div>
                  </div>
                  {renderFilterChildren()}
                </div>
              )}
              
              <div className="mt-12">
                <div className="d-flex align-items-center gap-2 mb-12">
                  <div className="flex-grow-1 border-bottom"></div>
                  <span className="text-secondary-light text-sm fw-medium px-2">Advanced Rules</span>
                  <div className="flex-grow-1 border-bottom"></div>
                </div>
                
                <div className="space-y-3">
                  {filters.map((filter, index) => {
                    const operators = getOperatorsForField(filter.field);
                    const isDateField = filter.field === 'DOB' || filter.field === 'DateOfBirth';
                    const fieldError = fieldErrors[index] || fieldErrors[`${index}-start`] || fieldErrors[`${index}-end`];
                    
                    return (
                      <div key={index} className={`row g-2 align-items-start p-2 rounded-3 ${fieldError ? 'border border-danger border-opacity-25' : ''}`} style={{ backgroundColor: index % 2 === 0 ? '#f8fafc' : 'transparent' }}>
                        <div className="col-md-3">
                          <select
                            className="form-select form-select-sm radius-8 border-1"
                            value={filter.field}
                            onChange={(e) => updateFilter(index, 'field', e.target.value)}
                            style={{ backgroundColor: '#fff' }}
                          >
                            <option value="">Select field</option>
                            {getAvailableFields(index).map((field) => (
                              <option key={field.value} value={field.value}>
                                {field.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-2">
                          <select
                            className="form-select form-select-sm radius-8 border-1"
                            value={filter.operator}
                            onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                            style={{ backgroundColor: '#fff' }}
                          >
                            {operators.map((op) => (
                              <option key={op.value} value={op.value}>
                                {op.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-5">
                          {renderFilterInput(filter, index)}
                        </div>

                        <div className="col-md-2 text-center d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger radius-8 d-inline-flex align-items-center justify-content-center"
                            onClick={() => removeFilter(index)}
                            disabled={filters.length === 1}
                            style={{ width: '32px', height: '32px', padding: 0, flexShrink: 0 }}
                          >
                            <Icon icon="ion:close-outline" className="icon" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  className="btn btn-outline-secondary btn-sm mt-12 d-inline-flex align-items-center gap-2 radius-8 px-16"
                  onClick={addFilter}
                >
                  <Icon icon="ion:add-outline" className="icon" />
                  Add Filter Rule
                </button>
              </div>
            </div>

            <div className="modal-footer py-12 px-20 border-top d-flex justify-content-between flex-wrap gap-2" style={{ backgroundColor: '#f8fafc' }}>
              <button
                className="btn btn-outline-danger btn-sm radius-8 px-20 d-inline-flex align-items-center gap-2"
                onClick={handleClearAll}
              >
                <Icon icon="ion:refresh-outline" className="icon" />
                Reset All
              </button>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm radius-8 px-20"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm radius-8 px-20 d-flex align-items-center gap-2"
                  onClick={handleApply}
                >
                  <Icon icon="ion:checkmark-outline" className="icon" />
                  {applyLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal {
          background-color: rgba(0, 0, 0, 0.5);
          overflow: hidden !important;
        }
        .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          max-height: 80vh;
        }
        .modal-header {
          border-bottom: 1px solid rgba(203, 213, 225, 0.6);
          flex-shrink: 0;
        }
        .modal-footer {
          border-top: 1px solid rgba(203, 213, 225, 0.6);
          flex-shrink: 0;
        }
        .modal-body {
          overflow-y: auto;
          flex: 1 1 auto;
        }
        .modal-body::-webkit-scrollbar {
          width: 6px;
        }
        .modal-body::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 8px;
        }
        .modal-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .modal-body::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .form-select, .form-control {
          border-color: #d1d5db;
          font-size: 13px;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }
        .form-select:focus, .form-control:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .form-control.is-invalid {
          border-color: #dc2626;
          padding-right: calc(1.5em + 0.75rem);
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc2626'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc2626' stroke='none'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right calc(0.375em + 0.1875rem) center;
          background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
        }
        .form-control.is-invalid:focus {
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
        .invalid-feedback {
          display: block;
          margin-top: 2px;
          font-size: 11px;
          color: #dc2626;
        }
        .btn-outline-primary {
          border-color: #d1d5db;
          color: #1e293b;
        }
        .btn-outline-primary:hover {
          background-color: #f1f5f9;
          border-color: #94a3b8;
          color: #0f172a;
        }
        .btn-primary {
          background-color: #2563eb;
          border-color: #2563eb;
        }
        .btn-primary:hover {
          background-color: #1d4ed8;
          border-color: #1d4ed8;
        }
        .btn-outline-danger {
          color: #dc2626;
          border-color: #dc2626;
        }
        .btn-outline-danger:hover {
          background-color: #fee2e2;
          border-color: #dc2626;
          color: #dc2626;
        }
        .btn-outline-secondary {
          color: #64748b;
          border-color: #d1d5db;
        }
        .btn-outline-secondary:hover {
          background-color: #f1f5f9;
          border-color: #94a3b8;
          color: #475569;
        }
        .badge {
          font-size: 10px;
          padding: 2px 6px;
          min-width: 18px;
        }
        .alert {
          border: none;
          border-radius: 8px;
          font-size: 13px;
          padding: 10px 16px;
        }
        .alert-danger {
          background-color: #fef2f2;
          color: #991b1b;
          border-left: 3px solid #dc2626;
        }
        .alert .btn-close {
          padding: 8px;
          font-size: 12px;
        }
        .border-danger {
          border-color: #dc2626 !important;
        }
        .border-opacity-25 {
          --bs-border-opacity: 0.25;
        }
        .date-input-wrapper {
          position: relative;
        }
        .date-input-wrapper input[type="date"] {
          padding-right: 32px;
        }
        .date-input-wrapper input[type="date"]::-webkit-calendar-picker-indicator {
          opacity: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: 32px;
          height: 100%;
          cursor: pointer;
        }
        .date-picker-icon {
          position: absolute;
          right: 4px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #64748b;
          padding: 4px 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 16px;
        }
        .date-picker-icon:hover {
          color: #2563eb;
        }
        .date-input-wrapper input[type="date"]::-moz-calendar-picker-indicator {
          opacity: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: 32px;
          height: 100%;
          cursor: pointer;
        }
        /* Fix for date inputs in between operator */
        .date-input-wrapper input[type="date"] {
          width: 100%;
          min-width: 0;
        }
        /* Ensure the remove button doesn't overlap */
        .btn-outline-danger {
          flex-shrink: 0;
        }
      `}</style>
    </>
  );
};

export default AdvancedFilter;